import React from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { BuilderChip } from '@/components/BuilderChip';
import {
  DetailSheet,
  DetailSheetActions,
  DetailSheetCol,
  DetailSheetEmpty,
  DetailSheetHead,
  DetailSheetLink,
  DetailSheetRow,
  DetailSheetSection,
  DetailSheetText,
  type DetailSheetActionItem,
} from '@/components/DetailSheet';
import { formatDate } from '@/utils/formatters';
import type { HtsQuestionnaire } from '@/services/api';

interface HtsQuestionnaireDetailSheetProps {
  questionnaire: HtsQuestionnaire;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onNavigateToQuestionSet: (id: string) => void;
}

export const HtsQuestionnaireDetailSheet: React.FC<HtsQuestionnaireDetailSheetProps> = ({
  questionnaire,
  onEdit,
  onDuplicate,
  onDelete,
  onNavigateToQuestionSet,
}) => {
  const questionCount = questionnaire.questions.length;
  const setCount = questionnaire.questionSets.length;

  const actions: DetailSheetActionItem[] = [
    {
      key: 'edit',
      label: 'Edit questionnaire',
      icon: <Pencil style={{ width: '1em', height: '1em' }} />,
      onClick: onEdit,
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy style={{ width: '1em', height: '1em' }} />,
      onClick: onDuplicate,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 style={{ width: '1em', height: '1em' }} />,
      danger: true,
      onClick: onDelete,
    },
  ];

  return (
    <DetailSheet>
      <DetailSheetHead
        title={questionnaire.name}
        tags={
          <>
            <BuilderChip tint="purple">HTS</BuilderChip>
            <BuilderChip tint="periwinkle">
              {questionCount} question{questionCount !== 1 ? 's' : ''}
            </BuilderChip>
            <BuilderChip tint="periwinkle">
              {setCount === 0 ? 'Not used' : `${setCount} set${setCount !== 1 ? 's' : ''}`}
            </BuilderChip>
          </>
        }
      />

      {questionnaire.description && (
        <DetailSheetSection label="Description">
          <DetailSheetText>{questionnaire.description}</DetailSheetText>
        </DetailSheetSection>
      )}

      <DetailSheetRow>
        <DetailSheetCol label="Created" value={formatDate(questionnaire.createdAt)} />
        <DetailSheetCol label="Updated" value={formatDate(questionnaire.updatedAt)} />
      </DetailSheetRow>

      <DetailSheetSection label="Used in">
        {setCount === 0 ? (
          <DetailSheetEmpty>Not used in any question set</DetailSheetEmpty>
        ) : (
          questionnaire.questionSets.map((qs) => (
            <DetailSheetLink key={qs.id} name={qs.name} onClick={() => onNavigateToQuestionSet(qs.id)} />
          ))
        )}
      </DetailSheetSection>

      <DetailSheetActions items={actions} />
    </DetailSheet>
  );
};

export default HtsQuestionnaireDetailSheet;
